import { FormMode, Marketplaces } from 'common/constants/enums';
import { IPageAsset, PageBannerAsset, PagePublisherImage, PageVideoAsset, Product } from 'common/interfaces/agg-page';
import { getProductsApiClient } from 'common/services/products-api.service';
import { AggPageFormContext, IProductContent, PageType } from 'context/page-form-context';
import { PagesContext } from 'context/pages-context';
import { PropsWithChildren, useContext, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useParams, useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { getAggPageApiClient } from '../../../common/services/agg-pages-api.service';
import { getProductsManagementApiClient } from '../../../common/services/products-management-api.service';
import { isAmazonProduct, isWalmartProduct } from '../../../common/type-guards';
import { utilProductDataPreparing } from '../../../common/utils';

export const UpdateWrapper = ({ children }: PropsWithChildren) => {
  const [params] = useSearchParams();
  const {
    brand,
    publisher: publisherName,
    slug,
  } = useParams();
  const marketplace = params.get('marketplace') as Marketplaces;
  const domain = params.get('domain');
  const layout = params.get('layout') || PageType.Brands;

  const formMode = params.get('mode') as FormMode;

  const {
    changeBrand,
    changePage,
    changePageLoading,
    changePublisher,
  } = useContext(PagesContext);

  const {
    addBannerImage,
    addContentCopies,
    addPageVideos,
    addProduct,
    addPublisherImages,
    changeBuyingGuide,
    changeDomainId,
    changeKeyword,
    changeMarketplace,
    changePageAuthor,
    changePageAuthorId,
    changePageId,
    changeProductsContent,
    changeSlug,
    changeSubtitle,
    setProductsAssets,
    setProductsReviews,
    updateDrafts,
    updatePageType,
    updateSocialPageSettings,
  } = useContext(AggPageFormContext);

  const fetchPageData = async () => {
    changePageLoading(true);
    try {
      const data: { brand?: string; slug: string } = {
        slug,
      };

      if (brand !== 'walmart') {
        data.brand = brand.replace(/-/g, ' ');
      }

      const response = layout === PageType.Social
      ? await getProductsApiClient(brand)
          .getSocialAttributionPage(
            {
              marketplace,
              slug,
            }
          )
      : await getProductsApiClient(publisherName)
          .getBrandAttributionPage(
            {
              ...data,
              marketplace,
              slug,
            },
            domain
          );

      const {
        additionalProducts,
        mainProduct,
        page,
        pageBrand,
        product,
      } = response.data;

      const pageAuthor = page.author;
      changePageAuthor(pageAuthor);
      changePageAuthorId(pageAuthor?.id);

      updatePageType(product ? PageType.Social : PageType.Brands);

      if (layout === PageType.Social) {
        updateSocialPageSettings('slideDuration', page.settings.slideDuration / 1000 || 3);
        updateSocialPageSettings('redirectTimeout', page.settings.redirectTimeout / 1000 || 10);
      }

      let productIdFormat: 'w_item_id' | 'asin';
      if (page.products_marketplace === Marketplaces.Walmart) {
        productIdFormat = 'w_item_id';
      }
      if (!page.products_marketplace || page.products_marketplace === Marketplaces.Amazon) {
        productIdFormat = 'asin';
      }

      if (page.products_marketplace) {
        changeMarketplace(page.products_marketplace);
      }

      const products: Product[] =
        ([!!mainProduct && mainProduct, ...(additionalProducts || []), !!product && product] as Product[])
          .filter((product) =>
            Object.hasOwn(product, productIdFormat));

      if (formMode !== FormMode.DUPLICATE) {
        changePageId(page.id);
        changeKeyword(page.title.trim());
        changeSubtitle(page.subtitle);
        changeSlug(page.slug);
      }

      changePage(page);

      if (page?.domain_id) {
        changeDomainId(page.domain_id);
      }

      changeBrand(pageBrand);

      let region = 'us'; // Default region
      if (publisherName && formMode !== FormMode.DUPLICATE) {
        const res = await getProductsApiClient()
          .getPublishersList(publisherName);
        // eslint-disable-next-line no-unsafe-optional-chaining
        const [publisher] = res.data?.publishers;

        changePublisher(publisher);
        region = publisher?.country || 'us';
      } else if (layout === PageType.Social) {
        const { data } = await getProductsApiClient()
          .getPublishersList();
        const publisher = data.publishers.find((elem) => elem.id === page.publisher_id);

        if (publisher) {
          changePublisher(publisher);
          region = publisher?.country || 'us';
        }
      }

      products.forEach((p) => {
        if (isWalmartProduct(p)) {
          p.product_id = p.w_item_id;
        }

        if (isAmazonProduct(p)) {
          p.product_id = p.asin;
        }

        addProduct(p);
      });

      products.forEach((product) => {
        const {
          assets,
          reviews,
        } = utilProductDataPreparing(product);

        setProductsReviews('add', reviews);
        setProductsAssets((prevState) => [
          ...prevState,
          {
            assets,
            product_id: product.product_id,
          },
        ]);
      });

      const contentCopiesResponse = await getProductsManagementApiClient()
        .getProductContentCopies(
          products.map((p) => p.product_id),
          region,
        );

      const contentCopies = contentCopiesResponse.data.products.reduce((result, elem) => {
        changeProductsContent({
          _id: uuidv4(),
          product_id: elem.asin,
          ...({
            ...elem.content,
            faq: elem.content?.faq || [],
            pros: elem.content?.pros?.filter((p) => p.trim().length > 0),
          } || {}),
          type: 'origin',
          tied: !products.find((p) => p.id === elem.id)?.picked_product_copy_id,
        });
        // @ts-ignore
        const copies: IProductContent[] = [];
        elem.product_copies.forEach((copy) => {
          if (copy.content) {
            copies.push({
              _id: uuidv4(),
              copy_id: copy.copy_id,
              product_id: elem.asin,
              tied: !!products.find((elem) => elem.picked_product_copy_id === copy.copy_id),
              ...{
                ...copy.content.content,
                faq: copy.content.content?.faq || [],
                pros: copy.content.content?.pros?.filter((p) => p.trim().length > 0) || [],
              },
            });
          }
        });

        result = [...result, ...copies];

        return result;
      }, []);

      addContentCopies(contentCopies);

      if (page?.video?.length) {
        const pageVideoAssets: PageVideoAsset[] = [];

        page.video.forEach((elem: { src: string, text: string }) => {
          pageVideoAssets.push({
            assets_type: 'video',
            file_data: {
              text: elem.text,
            },
            url: elem.src
          });
        });

        addPageVideos(pageVideoAssets);
      }

      const pagePublisherImagesAssets: PagePublisherImage[] = [];

      page.publisherImages?.forEach((item: IPageAsset) => {
        pagePublisherImagesAssets.push({
          assets_type: 'publisherImages',
          file_data: { order: item?.order },
          url: item.img,
        });
      });

      pagePublisherImagesAssets.sort((a, b) => a.file_data.order - b.file_data.order);

      addPublisherImages(pagePublisherImagesAssets);

      const pageBannerAssets: PageBannerAsset[] = [];

      page.assets?.forEach((item: IPageAsset) => {
        if (item.type === 'banner') {
          pageBannerAssets.push({
            assets_type: 'assets',
            file_data: {
              order: item?.order,
              type: 'banner',
            },
            url: item.img,
          });
        }
      });

      pageBannerAssets.sort((a, b) => a.file_data.order - b.file_data.order);

      addBannerImage(pageBannerAssets);

      if (page?.buyingGuide) {
        changeBuyingGuide({
          img: page.buyingGuide?.img || null,
          text: page.buyingGuide?.text || null,
          url: page.buyingGuide?.img || null,
        });
      }

      const draftsResponse = await getAggPageApiClient()
        .getPageDrafts(page.id);
      if (draftsResponse.status === 200) {
        updateDrafts(
          draftsResponse.data.drafts.map((elem) => ({
            ...elem,
            in_draft: Boolean(elem.in_draft),
          })),
        );
      }
    } catch (e: any) {
      let errMessage = '';
      if (e?.response?.status === 404) {
        errMessage = 'Page not found or hidden';
      } else {
        errMessage = e?.message;
      }

      toast.error(errMessage);
    } finally {
      changePageLoading(false);
    }
  };

  useEffect(() => {
    fetchPageData();
  }, []);

  return <div>{children}</div>;
};
